import { useState } from 'react'
import type { CashActionCard } from '../../api/cashReport'
import type { SaveState } from './saveState.ts'
import CrIcon from './CrIcon.tsx'
import { localDateKey } from '../../lib/localDate.ts'

type Props = {
  top: CashActionCard[]
  more: CashActionCard[]
  onDone: (card: CashActionCard) => Promise<void>
  onRemind: (card: CashActionCard, remindAt: string) => Promise<void>
  onHide: (card: CashActionCard) => Promise<void>
  onFix?: (card: CashActionCard) => void
}

function plusDays(n: number): string {
  const [y, m, d] = localDateKey().split('-').map(Number)
  const next = new Date(y, m - 1, d + n)
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`
}

function ActionCard({
  card, onDone, onRemind, onHide, onFix,
}: {
  card: CashActionCard
  onDone: Props['onDone']
  onRemind: Props['onRemind']
  onHide: Props['onHide']
  onFix?: Props['onFix']
}) {
  const [remind, setRemind] = useState(false)
  const [date, setDate] = useState(plusDays(7))
  const [save, setSave] = useState<SaveState>('idle')

  async function wrap(fn: () => Promise<void>) {
    setSave('saving')
    try {
      await fn()
      setSave('saved')
    } catch {
      setSave('error')
    }
  }

  return (
    <div className="cr-action" data-testid={`cr-action-${card.card_kind}`}>
      <div className="cr-action-main">
        <span className="cr-action-icon"><CrIcon name="bank" /></span>
        <p>{card.title}</p>
      </div>
      {card.fix_action && onFix ? (
        <button type="button" className="cr-btn dark" onClick={() => onFix(card)}>{card.fix_action}</button>
      ) : null}
      <div className="cr-action-buttons">
        <button type="button" className="cr-btn quiet" data-testid="cr-action-done" disabled={save === 'saving'} onClick={() => wrap(() => onDone(card))}>
          <CrIcon name="done" />{save === 'saving' ? 'Saving…' : save === 'saved' ? 'Saved' : 'Done'}
        </button>
        <button type="button" className="cr-btn quiet" data-testid="cr-action-remind" onClick={() => setRemind(true)}>
          <CrIcon name="remind" />Remind me
        </button>
        <button type="button" className="cr-btn quiet" data-testid="cr-action-hide" onClick={() => wrap(() => onHide(card))}>
          <CrIcon name="hide" />Don&apos;t show again
        </button>
      </div>
      {remind ? (
        <div className="cr-remind" data-testid="cr-action-remind-form">
          <input type="date" aria-label="Remind on" value={date} onChange={(e) => setDate(e.target.value)} />
          <button type="button" className="cr-btn dark" onClick={() => wrap(() => onRemind(card, date))}>Set reminder</button>
        </div>
      ) : null}
    </div>
  )
}

export default function ActionsBlock({ top, more, onDone, onRemind, onHide, onFix }: Props) {
  const [showMore, setShowMore] = useState(false)
  if (!top.length && !more.length) return null
  return (
    <section className="cr-section" data-testid="cr-actions">
      <div className="cr-section-label"><span>Actions</span><span>Up to three, then More</span></div>
      <div className="cr-panel">
        {top.map((card) => (
          <ActionCard key={card.fact_key} card={card} onDone={onDone} onRemind={onRemind} onHide={onHide} onFix={onFix} />
        ))}
        {more.length ? (
          <>
            <button type="button" className="cr-btn text" data-testid="cr-actions-more" aria-expanded={showMore} onClick={() => setShowMore((v) => !v)}>
              {showMore ? 'Hide more' : `More (${more.length})`}
            </button>
            {showMore ? more.map((card) => (
              <ActionCard key={card.fact_key} card={card} onDone={onDone} onRemind={onRemind} onHide={onHide} onFix={onFix} />
            )) : null}
          </>
        ) : null}
      </div>
    </section>
  )
}
